import React, { useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { AutoCompleteCompleteMethodParams } from 'primereact/autocomplete';
import packagedGoodsControlsService from '../../service/api/PackagedGoodsControlsService';
import FormErrorMessageScrollerRhf from '../_shared/FormErrorMessageScrollerRhf';
import { useTranslation } from 'react-i18next';
import { PackagedGoodsControl, ControlType, Status } from '../../types/PackagedGoodsControl';
import WriteOnlyButton from '../controls/WriteOnlyButton';
import enumService from '../../service/EnumService';
import suggestionsService from '../../service/api/SuggestionsService';
import { SuggestionOutput } from '../../types/Suggestion';
import { AutoCompleteField, DateField, DropdownField, NumberField, TextField } from '../_shared/fields';
import { Unit } from '../../types/Product';

interface FormValuesProps extends PackagedGoodsControl {
  productAutocomplete: SuggestionOutput | string;
  scaleAutocomplete: SuggestionOutput[];
}

const INIT_FORM_STATE: FormValuesProps = {
  id: null,
  name: '',
  controlType: null,
  productId: null,
  densityGMl: null,
  tareWeightG: null,
  lotNumber: '',
  plannedDate: null,
  startDate: null,
  endDate: null,
  lotQuantity: null,
  status: Status.PLANNED,
  result: null,
  t1Error: null,
  controlPhase1Quantity: null,
  controlTotalQuantity: null,
  scaleIds: null,
  productAutocomplete: null,
  scaleAutocomplete: null,
};

type Props = {
  dialogVisible: boolean;
  closeAddEditModal: VoidFunction;
  packagedGoodsControl: PackagedGoodsControl;

  showAddToast: (name: string) => void;
  showSaveToast: (name: string) => void;

  readOnly?: boolean;
};

function PackagedGoodsControlDialog({
  dialogVisible,
  closeAddEditModal,
  packagedGoodsControl,

  showAddToast,
  showSaveToast,

  readOnly,
}: Props) {
  const { t } = useTranslation();

  const methods = useForm<FormValuesProps>({
    defaultValues: INIT_FORM_STATE,
  });

  const { handleSubmit, reset, setError, watch, trigger, formState, setValue } = methods;

  const hideDialog = () => {
    reset(INIT_FORM_STATE);
    closeAddEditModal();
  };

  const controlTypes = useMemo(
    () => enumService.getTranslatedEnum(t, ControlType, 'packagedGoodsControls.controlTypes'),
    [t]
  );

  const statuses = useMemo(() => enumService.getTranslatedEnum(t, Status, 'packagedGoodsControls.statuses'), [t]);

  const [filteredProducts, setFilteredProducts] = useState([]);

  const searchProducts = async (event: AutoCompleteCompleteMethodParams) => {
    const query = event.query;
    const suggestions = await suggestionsService.getSuggestions({ query, entity: 'products' });
    setFilteredProducts(suggestions);
  };

  // Watch product selection to determine if density is required
  const productAutocomplete = watch('productAutocomplete');
  // Watch control type to determine if lot quantity is required
  const controlType = watch('controlType');

  // Set default density and trigger validation when product changes
  useEffect(() => {
    if (productAutocomplete) {
      const product = productAutocomplete as SuggestionOutput;
      const unit = product?.metadata?.unit;
      const densityGMl = product?.metadata?.densityGMl;

      // Clear density field if unit is not L or ML, otherwise set default from product
      if (unit && unit !== Unit.L && unit !== Unit.ML) {
        setValue('densityGMl', null);
      } else if (densityGMl !== undefined) {
        setValue('densityGMl', densityGMl);
      }

      // Trigger validation if form was submitted
      if (formState.isSubmitted) {
        trigger('densityGMl');
      }
    }
  }, [productAutocomplete, trigger, formState.isSubmitted, setValue]);

  // Trigger lot quantity validation when control type changes
  useEffect(() => {
    if (formState.isSubmitted) {
      trigger('lotQuantity');
    }
  }, [controlType, trigger, formState.isSubmitted]);

  const [filteredScales, setFilteredScales] = useState([]);

  const searchScales = async (event: AutoCompleteCompleteMethodParams) => {
    const query = event.query;
    const suggestions = await suggestionsService.getSuggestions({ query, entity: 'scales' });
    setFilteredScales(suggestions);
  };

  const editMode = !!packagedGoodsControl;
  useEffect(() => {
    if (dialogVisible && editMode) {
      reset({
        ...packagedGoodsControl,
        productAutocomplete: packagedGoodsControl.productId
          ? {
              id: packagedGoodsControl.productId,
              value: packagedGoodsControl.productName,
              metadata: { unit: packagedGoodsControl.unit },
            }
          : null,
        scaleAutocomplete: packagedGoodsControl.scales ?? [],
      });
    } else {
      reset(INIT_FORM_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogVisible]);

  const onSubmit = async (formData: FormValuesProps) => {
    try {
      if (!formData.id) {
        await packagedGoodsControlsService.create(formData);

        showAddToast(formData.name);
      } else {
        await packagedGoodsControlsService.edit(formData, formData.id);

        showSaveToast(formData.name);
      }

      hideDialog();
    } catch (error: any) {
      if (error.response.status === 409) {
        const field = error.response.data.field;
        setError(field, { type: 'manual', message: t(`packagedGoodsControls.error_${field}_already_taken`) });
      }
    }
  };

  const DialogFooter = (
    <>
      <Button
        type="reset"
        label={t('common.cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />

      <WriteOnlyButton
        disabled={readOnly ? true : undefined}
        type="submit"
        label={t('common.save')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={handleSubmit(onSubmit)}
      />
    </>
  );

  return (
    <Dialog
      visible={dialogVisible}
      header={t('packagedGoodsControls.packaged_goods_control_details')}
      modal
      className="p-fluid"
      footer={DialogFooter}
      onHide={hideDialog}
      breakpoints={{ '1400px': '60vw', '896px': '90vw' }}
      style={{ width: '40vw' }}
    >
      <FormProvider {...methods}>
        <FormErrorMessageScrollerRhf>
          <div className="col-10">
            <TextField
              containerClass=""
              inputName={`name`}
              inputLabel={t('packagedGoodsControls.name')}
              disabled={readOnly ? true : undefined}
              rules={{
                validate: (value) => {
                  if (!value?.trim()) {
                    return t('packagedGoodsControls.error_name_required');
                  }
                  return true;
                },
              }}
            />

            <DropdownField
              containerClass=""
              inputName={`controlType`}
              inputLabel={t('packagedGoodsControls.control_type')}
              disabled={readOnly ? true : undefined}
              options={controlTypes}
              placeholder={t('common.select')}
              rules={{
                validate: (value) => {
                  if (value == null) {
                    return t('packagedGoodsControls.error_control_type_required');
                  }
                  return true;
                },
              }}
            />

            <AutoCompleteField
              containerClass=""
              inputName={`productAutocomplete`}
              inputLabel={t('packagedGoodsControls.product')}
              disabled={readOnly ? true : undefined}
              suggestions={filteredProducts}
              idFieldName="productId"
              field="value"
              dropdown
              completeMethod={searchProducts}
              placeholder={t('common.select')}
              rules={{
                validate: (value) => {
                  if (!value) {
                    return t('packagedGoodsControls.error_product_required');
                  }
                  return true;
                },
              }}
            />

            {(() => {
              const product = productAutocomplete as SuggestionOutput;
              const unit = product?.metadata?.unit;
              return (
                (unit === Unit.L || unit === Unit.ML) && (
                  <NumberField
                    containerClass=""
                    inputName={`densityGMl`}
                    inputLabel={t('packagedGoodsControls.density_g_ml')}
                    disabled={readOnly ? true : undefined}
                    maxFractionDigits={5}
                    rules={{
                      validate: (value) => {
                        const product = productAutocomplete as SuggestionOutput;
                        const unit = product?.metadata?.unit;

                        // Require density for liquid units (L or ML)
                        if ((unit === Unit.L || unit === Unit.ML) && value == null) {
                          return t('packagedGoodsControls.error_density_required');
                        }
                        return true;
                      },
                    }}
                  />
                )
              );
            })()}

            {/* <NumberField
              containerClass=""
              inputName={`tareWeightG`}
              inputLabel={t('packagedGoodsControls.tare_weight_g')}
              disabled={readOnly ? true : undefined}
              maxFractionDigits={5}
            /> */}

            <TextField
              containerClass=""
              inputName={`lotNumber`}
              inputLabel={t('packagedGoodsControls.lot_number')}
              disabled={readOnly ? true : undefined}
              rules={{
                validate: (value) => {
                  if (!value?.trim()) {
                    return t('packagedGoodsControls.error_lot_number_required');
                  }
                  return true;
                },
              }}
            />

            <DateField
              containerClass=""
              inputName={`plannedDate`}
              inputLabel={t('packagedGoodsControls.planned_date')}
              disabled={readOnly ? true : undefined}
              showTime
              showSeconds
            />

            <NumberField
              containerClass=""
              inputName={`lotQuantity`}
              inputLabel={t('packagedGoodsControls.lot_quantity')}
              disabled={readOnly ? true : undefined}
              maxFractionDigits={0}
              rules={{
                validate: (value) => {
                  // Require lot quantity for SAMPLE control type
                  if (controlType === ControlType.SAMPLE) {
                    if (value == null || value <= 0) {
                      return t('packagedGoodsControls.error_lot_quantity_required');
                    }
                    if (value < 100) {
                      return t('packagedGoodsControls.error_lot_quantity_min_100');
                    }
                  }
                  return true;
                },
              }}
            />

            <DropdownField
              containerClass=""
              inputName={`status`}
              inputLabel={t('packagedGoodsControls.status')}
              disabled={true}
              options={statuses}
              placeholder={t('common.select')}
              rules={{
                validate: (value) => {
                  if (value == null) {
                    return t('packagedGoodsControls.error_status_required');
                  }
                  return true;
                },
              }}
            />

            <AutoCompleteField
              containerClass=""
              inputName={`scaleAutocomplete`}
              inputLabel={t('packagedGoodsControls.scale')}
              disabled={readOnly ? true : undefined}
              suggestions={filteredScales}
              idFieldName="scaleIds"
              field="value"
              dropdown
              multiple={true}
              completeMethod={searchScales}
              placeholder={t('common.select')}
            />
          </div>
        </FormErrorMessageScrollerRhf>
      </FormProvider>
    </Dialog>
  );
}

export default PackagedGoodsControlDialog;
